import {
  GENERATE_QUESTIONS_REQUEST,
  GENERATE_QUESTIONS_SUCCESS,
  GENERATE_QUESTIONS_FAILURE,
  APPROVE_GENERATED_QUESTION,
  REJECT_GENERATED_QUESTION,
  EDIT_GENERATED_QUESTION,
  UPDATE_GENERATED_PASSAGE,
  CLEAR_GENERATED_QUESTIONS
} from './AIGenerationTypes';
import { createQuestion } from './QuestionsAction';
import { createPassage } from './PassageAction';
import { v4 as uuidv4 } from 'uuid';
import { generateQuestions } from '../../util/aiService';
import { generateImage } from '../../util/imageService';

/**
 * Extract plain text from Draft.js raw content state
 */
const extractTextFromDraftRaw = (draftRaw) => {
  if (!draftRaw || !draftRaw.blocks) return '';
  return draftRaw.blocks.map(block => block.text).join(' ');
};

/**
 * Extract key concepts from passage text for figure generation
 */
const extractKeyConceptsForFigure = (passageText, topic, maxLength = 300) => {
  // Get first few sentences that likely contain the main concept
  const sentences = passageText.split(/[.!?]+/).filter(s => s.trim().length > 10);
  const keyContent = sentences.slice(0, 3).join('. ').substring(0, maxLength);
  return keyContent || topic;
};

/**
 * Generate questions using AI
 * @param {Object} params - Generation parameters
 * @param {string} params.subjectId - Subject ID
 * @param {string} params.unitId - Unit ID
 * @param {string} params.topicId - Topic ID
 * @param {string} params.aiModel - AI model to use ('gemini' or 'chatgpt')
 * @param {number} params.count - Number of questions to generate (default: 10)
 * @param {number} params.numberOfChoices - Number of choices per question (default: 4)
 * @param {boolean} params.isPassageBased - Whether to generate a new passage
 * @param {string} params.passageMode - 'new' or 'existing'
 * @param {string} params.subjectName - Subject name (for prompt)
 * @param {string} params.unitName - Unit name (for prompt)
 * @param {string} params.topicName - Topic name (for prompt)
 * @param {string} userEmail - User email
 * @param {Object} existingPassage - Existing passage to use (optional)
 */
export const generateQuestionsWithAI = (params, userEmail, existingPassage = null) => async (dispatch) => {
  dispatch({ type: GENERATE_QUESTIONS_REQUEST });

  try {
    // Call the real AI service
    const aiResult = await generateQuestions(params, existingPassage);

    // Add temporary IDs and metadata to generated questions
    const questionsWithMetadata = aiResult.questions.map(q => ({
      ...q,
      tempId: uuidv4(),
      generatedAt: new Date().toISOString(),
      generatedBy: userEmail,
      status: 'pending_review',
      subjectId: params.subjectId,
      unitId: params.unitId,
      ...(params.topicId ? { topicId: params.topicId } : {}),
      tags: params.tags || [],
      source: 'ai_generated'
    }));

    // Generate figure if requested and passage exists
    let passageWithFigure = aiResult.passage || null;
    if (params.generateFigure && passageWithFigure && passageWithFigure.passageText) {
      try {
        // Extract passage content to create a contextually relevant figure
        const passageText = extractTextFromDraftRaw(passageWithFigure.passageText);
        const topic = params.topicName || params.unitName || 'medical science';
        const passageTitle = passageWithFigure.title || topic;

        // Extract key concepts from the passage for a more relevant figure
        const keyContent = extractKeyConceptsForFigure(passageText, topic);

        const figurePrompt = `Simple scientific diagram about ${passageTitle}. Topic: ${keyContent.substring(0, 150)}`;

        const figureUrl = await generateImage(figurePrompt);

        // Add figure to passage
        passageWithFigure = {
          ...passageWithFigure,
          figureUrl: figureUrl,
          figureDescription: `Figure 1: ${passageTitle}`
        };
      } catch (figureError) {
        console.error('Figure generation failed:', figureError);
        // Continue without figure - don't fail the whole operation
      }
    }

    // Prepare payload with passage if it exists
    const payload = {
      questions: questionsWithMetadata,
      passage: passageWithFigure
    };

    dispatch({
      type: GENERATE_QUESTIONS_SUCCESS,
      payload: payload
    });

    return { success: true, questions: questionsWithMetadata, passage: passageWithFigure };
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to generate questions';

    dispatch({
      type: GENERATE_QUESTIONS_FAILURE,
      payload: errorMessage
    });

    return { success: false, error: errorMessage };
  }
};

/**
 * Approve and save a generated question
 * If passage-based, create passage first then link question to it
 */
export const approveGeneratedQuestion = (question, passage, userEmail, subjectName = null) => async (dispatch) => {
  try {
    // Remove temporary fields before saving
    const { tempId, generatedAt, generatedBy, status, tempPassageId, ...questionData } = question;

    let finalQuestionData = questionData;
    // M8: Work with a shallow copy to avoid mutating Redux state directly
    let passageCopy = passage ? { ...passage } : null;

    // If this is a passage-based question and passage exists
    if (question.isPassageBased && passageCopy && question.tempPassageId === passageCopy.tempPassageId) {
      // Check if passage was already created (has real passageId)
      if (!passageCopy.passageId) {
        // Create the passage first
        const { tempPassageId: tempPsgId, ...passageData } = passageCopy;
        const passageResult = await dispatch(createPassage(passageData, userEmail));

        if (!passageResult.success) {
          return { success: false, error: `Failed to create passage: ${passageResult.error}` };
        }

        // Store the real passageId on the copy for future questions
        passageCopy.passageId = passageResult.passageId;

        // Update Redux state so subsequent approvals see the real passageId
        dispatch({
          type: UPDATE_GENERATED_PASSAGE,
          payload: { passageId: passageResult.passageId }
        });
      }

      // Link question to the passage
      finalQuestionData = {
        ...questionData,
        passageId: passageCopy.passageId,
        isPassageBased: true
      };
    }

    // Create the question in the database
    const result = await dispatch(createQuestion(finalQuestionData, userEmail, subjectName));

    if (result.success) {
      // Remove from generated questions list
      dispatch({
        type: APPROVE_GENERATED_QUESTION,
        payload: tempId
      });

      return { success: true };
    } else {
      return { success: false, error: result.error };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Reject a generated question
 */
export const rejectGeneratedQuestion = (tempId) => (dispatch) => {
  dispatch({
    type: REJECT_GENERATED_QUESTION,
    payload: tempId
  });

  return { success: true };
};

/**
 * Edit a generated question before approval
 */
export const editGeneratedQuestion = (tempId, updatedQuestion) => (dispatch) => {
  dispatch({
    type: EDIT_GENERATED_QUESTION,
    payload: {
      tempId,
      question: updatedQuestion
    }
  });

  return { success: true };
};

/**
 * Clear all generated questions
 */
export const clearGeneratedQuestions = () => (dispatch) => {
  dispatch({ type: CLEAR_GENERATED_QUESTIONS });
};
